import { Account } from '@packages/models/api';

/**
 * Formats a byte count into a human-readable string with appropriate units
 * (Bytes, KB, MB, GB, TB). Returns '0 Bytes' for zero or negative values.
 *
 * @param bytes - the number of bytes to format
 * @param decimals - the number of decimal places to include (default 2; clamped to 0 minimum)
 *
 * @example
 * ```ts
 * formatBytes(1024);       // '1 KB'
 * formatBytes(1536, 1);    // '1.5 KB'
 * formatBytes(0);          // '0 Bytes'
 * ```
 *
 * @returns a formatted string like '1.5 MB'
 */
export function formatBytes(bytes: number, decimals = 2): string {
    if (bytes <= 0) {
        return '0 Bytes';
    }

    const k = 1024;
    const dm = Math.max(0, decimals);
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Returns the initials (first letter of first name + first letter of last name) for an account.
 *
 * @param account - the account to extract initials from
 *
 * @example
 * ```ts
 * getAccountInitials({ firstName: 'Luke', lastName: 'Chavers', ... }); // 'LC'
 * ```
 *
 * @returns a two-character string of uppercase initials
 */
export function getAccountInitials(account: Account) {
    return `${account.firstName[0]}${account.lastName[0]}`;
}

/**
 * Extracts the file extension from a filename or path, in lowercase, without the leading dot.
 * Returns an empty string if the filename has no extension.
 *
 * @param filename - the filename or path to extract the extension from
 *
 * @example
 * ```ts
 * getExtension('report.PDF');    // 'pdf'
 * getExtension('photo.jpg');     // 'jpg'
 * getExtension('noext');         // ''
 * getExtension('.hidden');       // 'hidden'
 * ```
 *
 * @returns the lowercase extension without the dot, or an empty string
 */
export function getExtension(filename: string): string {
    return (filename.split('.').pop() || '').toLowerCase();
}

/**
 * Checks whether a filename has the given extension (case-insensitive).
 *
 * @param filename - the filename or path to check
 * @param ext - the extension to match, without the leading dot (e.g. 'pdf', 'sepf')
 *
 * @example
 * ```ts
 * hasExtension('report.PDF', 'pdf');   // true
 * hasExtension('photo.jpg', 'png');    // false
 * hasExtension('noext', 'txt');        // false
 * ```
 *
 * @returns true if the filename ends with the given extension
 */
export function hasExtension(filename: string, ext: string): boolean {
    return getExtension(filename) === ext.toLowerCase();
}

/**
 * A no-op function. Useful as a default callback or placeholder where a function reference
 * is required but no action is needed.
 *
 * @example
 * ```ts
 * const handleChange = onChange ?? noop;
 * ```
 */
export function noop() {}

/**
 *  Convert a value that may be a boolean, a string, null, or undefined into a boolean.
 *  Interprets the strings "true" (case-insensitive) and "false" (case-insensitive) as their
 *  respective boolean values;
 */
export function toBoolean(value: boolean | string | null | undefined): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
            return true;
        }
        if (normalized === 'false') {
            return false;
        }
    }

    return false;
}
